import type { FC } from "react";

import { useConfig } from "@jti/app-context/useConfig";
import type { Address, AddressFormVariant } from "@jti/fe-common";

import { AddressStreetInfoExtendedAsianVariant } from "./AddressStreetInfoExtendedAsianVariant";
import { AddressStreetInfoJordanVariant } from "./AddressStreetInfoJordanVariant";
import { AddressStreetInfoSaudiArabiaVariant } from "./AddressStreetInfoSaudiArabiaVariant";
import { AddressStreetInfoSingleFieldVariant } from "./AddressStreetInfoSingleFieldVariant";
import { AddressStreetInfoStandardVariant } from "./AddressStreetInfoStandardVariant";
import { AddressStreetInfoTaiwan } from "./AddressStreetInfoTaiwan";
import { AddressStreetInfoUnitedArabEmirates } from "./AddressStreetInfoUnitedArabEmirates";
import { AddressStreetInfoWithProvince } from "./AddressStreetInfoWithProvince";
import { AddressStreetInfoWithRegion } from "./AddressStreetInfoWithRegion";

import styles from "./AddressInfo.module.scss";

export type AddressStreetInfoProps = {
  address: Address;
  hidePostCode?: boolean;
  variant?: AddressFormVariant;
  isStreetFieldCombined?: boolean;
};

export const AddressStreetInfoContent: FC<AddressStreetInfoProps> = ({
  address,
  variant = "standard",
  hidePostCode = false,
  isStreetFieldCombined = false,
}) => {
  switch (variant) {
    case "single_field_lookup":
      return (
        <AddressStreetInfoSingleFieldVariant
          postcode={address.postcode}
          streetName={address.streetName}
          streetAdditional={address.streetAdditional}
        />
      );
    case "extended_asian":
    case "extended_asian_reversed":
      return (
        <AddressStreetInfoExtendedAsianVariant
          streetNumber={address.streetNumber}
          streetName={address.streetName}
          streetAdditional={address.streetAdditional}
          districtName={address.districtName ?? ""}
          provinceName={address.provinceName ?? ""}
          city={address.city}
          postcode={address.postcode}
          countryName={address.countryName ?? ""}
        />
      );
    case "saudi_arabia":
      return (
        <AddressStreetInfoSaudiArabiaVariant
          shortAddress={address.shortAddress}
          postcode={address.postcode}
          provinceName={address.provinceName}
          city={address.city}
          district={address.district}
          streetName={address.streetName}
          streetAdditional={address.streetAdditional}
          countryName={address.countryName}
        />
      );
    case "united_arab_emirates":
      return (
        <AddressStreetInfoUnitedArabEmirates
          city={address.city}
          premise={address.premise}
          landmark={address.landmark}
          streetName={address.streetName}
          neighborhood={address.neighborhood}
          streetAdditional={address.streetAdditional}
        />
      );
    case "jordan":
      return (
        <AddressStreetInfoJordanVariant
          city={address.city}
          streetName={address.streetName}
          streetNumber={address.streetNumber}
          streetAdditional={address.streetAdditional}
        />
      );
    case "with_regions":
    case "with_regions_reversed":
      return (
        <AddressStreetInfoWithRegion
          streetNumber={address.streetNumber}
          streetName={address.streetName}
          streetAdditional={address.streetAdditional}
          city={address.city}
          postcode={address.postcode}
          regionName={address.region?.region ?? ""}
          countryName={address.countryName ?? ""}
          hidePostCode={hidePostCode}
          isReversed={variant === "with_regions_reversed"}
        />
      );
    case "taiwan":
      return <AddressStreetInfoTaiwan address={address} />;

    case "extended_with_province":
    case "extended_with_province_reversed":
      return (
        <AddressStreetInfoWithProvince
          streetName={address.streetName}
          streetNumber={address.streetNumber}
          streetAdditional={address.streetAdditional}
          city={address.city}
          postcode={address.postcode}
          countryName={address.countryName ?? ""}
          provinceName={address.provinceName ?? ""}
          isReversed={variant === "extended_with_province_reversed"}
          isStreetFieldCombined={isStreetFieldCombined}
        />
      );
    default:
      return (
        <AddressStreetInfoStandardVariant
          streetNumber={address.streetNumber}
          streetName={address.streetName}
          streetAdditional={address.streetAdditional}
          city={address.city}
          postcode={address.postcode}
          countryName={address.countryName ?? ""}
          hidePostCode={hidePostCode}
          isReversed={variant === "standard_reversed"}
        />
      );
  }
};

export const AddressStreetInfo: FC<AddressStreetInfoProps> = ({
  address,
  variant,
  hidePostCode = false,
  isStreetFieldCombined = false,
}) => {
  const config = useConfig();

  return (
    <div className={styles.addressInfo}>
      <AddressStreetInfoContent
        address={address}
        variant={variant ?? config.addressFormVariant}
        hidePostCode={hidePostCode}
        isStreetFieldCombined={isStreetFieldCombined}
      />
    </div>
  );
};
